import * as Sentry from '@sentry/react'
import { graphqlClient } from '@/core/api/graphql-client'
import { MarkExperimentExposedDocument } from '@/generated/graphql'

// Keyed by user and experiment, never by plan: the plan is context on the report, and the
// server enrols once per user per experiment whatever it carries, so a plan switch mid-session
// would only buy a second request the server refuses. Keyed by user at all because logout does
// not reload the page. Inserted before the request so two renders cannot both send, and dropped
// again if that request fails: a lost report enrols nobody, so the latch may only hold what
// reached the server.
const reported = new Set<string>()

interface ExposureReport {
  userId: string
  /**
   * The plan this opening happened on. Null on an unanchored surface reached without one —
   * requiring it there would drop planless users, who are in the experiment like anyone else.
   */
  planId: string | null
  experimentName: string
  /**
   * What the serve ANSWER said, never what the client managed to render: only one arm can fail a
   * render, so deriving this from the UI would filter that arm on device health.
   *
   * Omitted where the server assigned the arm itself and can read it back from its own record —
   * an arm merely read from config is a claim that can disagree with the assignment it came from.
   */
  arm?: string
}

// The plan travels with the report: the server cannot re-derive it, because the
// active plan can move while the enrolment it triggers is still open.
export function markExperimentExposed({
  userId,
  planId,
  experimentName,
  arm,
}: ExposureReport): void {
  const key = `${userId}|${experimentName}`
  if (reported.has(key)) return
  reported.add(key)

  graphqlClient
    .request(MarkExperimentExposedDocument, { experimentName, planId, arm })
    .catch((error) => {
      // Released so a later render can try again, the way Android's next plan entry does.
      reported.delete(key)
      Sentry.captureException(error, {
        tags: { feature: 'experiments', stage: 'markExperimentExposed' },
      })
    })
}
